import React, { useState, useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import { useAuth } from "@/context/AuthContext";
import { hasAccessToSoftwareTurbinado } from "@/utils/predictionUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, AlertTriangle, Crown, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ResultadoForm from "@/components/software/ResultadoForm";
import ConfigForm from "@/components/software/ConfigForm";
import ResultadosTable from "@/components/software/ResultadosTable";
import AnimalGroupsTable from "@/components/software/AnimalGroupsTable";
import JogosGeradosModal from "@/components/software/JogosGeradosModal";
import DaysSelector from "@/components/pages/software-turbinado/DaysSelector";
import { useSoftwareTurbinado } from "@/hooks/useSoftwareTurbinado";
import MultiStepResultadoModal from "@/components/software/MultiStepResultadoModal";
import PixPaymentModal from "@/components/PixPaymentModal";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import PageInstructions from "@/components/PageInstructions";

const SoftwarePage: React.FC = () => {
  const { user, isAuthenticated, getTrialInfo } = useAuth();
  const [showResultForm, setShowResultForm] = useState(false);
  const [showAnimalGroups, setShowAnimalGroups] = useState(false);
  const [userPlan, setUserPlan] = useState<string>('free');
  const [showClearByDay, setShowClearByDay] = useState(false);
  const [dateToClear, setDateToClear] = useState<string>("");
  const { 
    resultados,
    config,
    gerarJogos, 
    isLoading, 
    showJogosModal, 
    setShowJogosModal, 
    jogosGerados,
    selectedDays,
    setSelectedDays,
    saveResultado,
    saveMultipleResultados,
    saveConfig,
    isTrialExpired,
    clearAllResultados,
    clearResultadoByDate,
  } = useSoftwareTurbinado();

  // Verificar o plano do usuário em tempo real
  useEffect(() => {
    const checkUserPlan = async () => {
      if (!user?.id) return;

      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("plan, created_at, is_subscribed")
          .eq("id", user.id)
          .single();

        if (profile) {
          console.log('Plano do usuário:', profile);
          setUserPlan(profile.plan || 'free');
        }
      } catch (error) {
        console.error("Erro ao verificar plano do usuário:", error);
      }
    };

    if (isAuthenticated && user) {
      checkUserPlan();
    }
  }, [user, isAuthenticated]);

  const trialInfo = getTrialInfo();
  // CRÍTICO: Durante o acesso válido o usuário tem acesso
  const isAccessExpired = trialInfo.status === 'trial_expired' || trialInfo.status === 'subscription_expired';
  const hasAccess = isAuthenticated && user && (!isAccessExpired && trialInfo.isActive) || hasAccessToSoftwareTurbinado(userPlan as any);

  const navigate = useNavigate();
  useEffect(() => {
    if (isAuthenticated && user && userPlan === 'free' && isAccessExpired) {
      navigate('/planos');
    }
  }, [isAuthenticated, user, userPlan, isAccessExpired, navigate]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4">Software Turbinado</h1>
            <Card>
              <CardContent className="pt-6">
                <Lock className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  Você precisa estar logado para acessar o Software Turbinado.
                </p>
                <Link to="/login">
                  <Button>Fazer Login</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Software Turbinado é APENAS para planos pagos
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <PixPaymentModal />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4">Software Turbinado</h1>
            <Card>
              <CardContent className="pt-6">
                <Lock className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  O Software Turbinado está disponível apenas para planos <strong>Premium</strong>.
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  Seu plano atual: <span className="font-medium">{
                    userPlan === 'free' ? 'Gratuito' :
                    userPlan === 'basic' ? 'Mensal' :
                    userPlan === 'premium' ? 'Trimestral' :
                    userPlan === 'vip' ? 'Anual' : 
                    userPlan === 'quarterly' ? 'Trimestral' : 'Indefinido'
                  }</span>
                </p>
                <Link to="/planos">
                  <Button>Fazer Upgrade</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const handleGerarJogos = async () => {
    if (isTrialExpired) {
      toast.error("Seu período de teste de 3 dias expirou! Faça upgrade para gerar jogos.", {
        duration: 6000,
      });
      return;
    }

    if (resultados.length < selectedDays) {
      alert(`É necessário ter pelo menos ${selectedDays} dias de resultados para gerar jogos otimizados.`);
      return;
    }
    await gerarJogos();
  };

  const handleClearAll = async () => {
    if (confirm("Tem certeza que deseja limpar TODOS os resultados?")) {
      await clearAllResultados();
    }
  };

  const handleConfirmClearByDay = async () => {
    if (!dateToClear) {
      toast.error("Selecione um dia para limpar.");
      return;
    }
    const ok = await clearResultadoByDate(dateToClear);
    if (ok) setShowClearByDay(false);
  };

  const availableDates = Array.from(new Set(resultados.map((r) => r.data).filter(Boolean)));

  const titulos = {
    ppt: config?.titulo_ppt || "PPT",
    ptm: config?.titulo_ptm || "PTM", 
    pt: config?.titulo_pt || "PT",
    ptv: config?.titulo_ptv || "PTV",
    ptn: config?.titulo_ptn || "PTN",
    coruja: config?.titulo_coruja || "CORUJA"
  };

  return (
    <>
      <SEOHead
        title="Software Turbinado Jogo do Bicho - Análise e Geração de Jogos"
        description="Software avançado para o Jogo do Bicho. Insira resultados dos últimos dias e gere jogos otimizados com análise inteligente de padrões e tendências."
        keywords="software jogo do bicho, gerador de apostas bicho, software turbinado, análise jogo do bicho, gerador de jogos bicho"
        canonical="/software"
      />
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <PixPaymentModal />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Arial Black, Arial, sans-serif' }}>
              SOFTWARE TURBINADO
            </h1>
            
            <p className="text-muted-foreground text-lg mb-6">
              Insira os resultados dos últimos {selectedDays} dias e gere jogos otimizados para apostar por 5 dias
            </p>

            <PageInstructions
              title="📖 Como usar o Software Turbinado"
              steps={[
                "Escolha quantos dias de resultados anteriores você quer inserir (3, 5 ou 7 dias)",
                "Clique em 'Adicionar Resultado' e insira os resultados de cada turno (PTM, PT, PTV, etc.)",
                "Após inserir todos os resultados, clique em 'Gerar Jogos'",
                "O sistema analisa os padrões e gera jogos otimizados para os próximos 5 dias",
                "Consulte a tabela de 'Grupos dos Bichos' se precisar saber qual grupo corresponde a cada animal"
              ]}
            />
            
            <div className="flex justify-center items-center gap-4 mb-6 flex-wrap">
              <DaysSelector 
                selectedDays={selectedDays}
                onDaysChange={setSelectedDays}
              />
              <Button
                variant="outline"
                onClick={() => setShowAnimalGroups(true)}
                className="flex items-center gap-2"
              >
                <Info className="h-4 w-4" />
                Grupos dos Bichos
              </Button>
              <Button 
                variant="secondary"
                onClick={() => setShowClearByDay(true)}
              >
                Limpar por dia
              </Button>
              <Button 
                variant="destructive"
                onClick={handleClearAll}
              >
                Limpar tudo
              </Button>
            </div>
          </div>

          <div className="mb-8">
            <ResultadosTable 
              resultados={resultados}
              config={config}
              selectedDays={selectedDays}
              onAddResult={() => setShowResultForm(true)}
            />
          </div>

          <div className="text-center mb-8">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl flex items-center justify-center gap-2">
                  GERAR JOGOS TURBINADOS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-6">
                  Com base nos resultados inseridos, o software vai gerar jogos otimizados para você apostar por 5 dias consecutivos.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 text-sm">
                  <div className="bg-gradient-to-r from-purple-500 to-blue-500 text-white p-3 rounded">
                    <div className="font-bold">12</div>
                    <div>MILHAR</div>
                  </div>
                  <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-3 rounded">
                    <div className="font-bold">12</div>
                    <div>CENTENAS</div>
                  </div>
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-3 rounded">
                    <div className="font-bold">7</div>
                    <div>DEZENAS</div>
                  </div>
                  <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white p-3 rounded">
                    <div className="font-bold">7</div>
                    <div>TERNOS</div>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white p-3 rounded">
                    <div className="font-bold">7</div>
                    <div>DUQUES</div>
                  </div>
                  <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-3 rounded">
                    <div className="font-bold">7</div>
                    <div>DUPLAS GRUPO</div>
                  </div>
                  <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white p-3 rounded">
                    <div className="font-bold">7</div>
                    <div>TERNOS GRUPO</div>
                  </div>
                  <div className="bg-gradient-to-r from-emerald-500 to-green-500 text-white p-3 rounded">
                    <div className="font-bold">PDF + IMG</div>
                    <div>DOWNLOAD</div>
                  </div>
                </div>
                <Button 
                  onClick={handleGerarJogos}
                  disabled={isLoading || resultados.length < selectedDays}
                  size="lg"
                  className="btn-gradient text-lg px-8 py-4"
                  style={{ fontFamily: 'Arial Black, Arial, sans-serif' }}
                >
                  {isLoading ? "GERANDO JOGOS..." : "GERAR JOGOS TURBINADOS"}
                </Button>
                {resultados.length < selectedDays && (
                  <p className="text-red-500 text-sm mt-2">
                    Você precisa inserir pelo menos {selectedDays} resultados para gerar jogos turbinados
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="configuracoes" className="max-w-4xl mx-auto mt-8">
            <TabsList className="grid w-full grid-cols-1">
              <TabsTrigger value="configuracoes">Personalizar Títulos dos Horários</TabsTrigger>
            </TabsList>

            <TabsContent value="configuracoes" className="mt-6">
              <ConfigForm 
                config={config}
                onSave={saveConfig}
                isLoading={isLoading}
              />
            </TabsContent>
          </Tabs>

          <MultiStepResultadoModal
            open={showResultForm}
            onClose={() => setShowResultForm(false)}
            onSave={saveMultipleResultados}
            isLoading={isLoading}
            selectedDays={selectedDays}
            titulos={titulos}
          />

          <Dialog open={showClearByDay} onOpenChange={setShowClearByDay}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Limpar resultados por dia</DialogTitle>
              </DialogHeader>
              {availableDates.length === 0 ? (
                <p className="text-muted-foreground">Não há resultados para limpar.</p>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Selecione o dia</label>
                    <Select value={dateToClear} onValueChange={setDateToClear}>
                      <SelectTrigger className="w-full mt-1">
                        <SelectValue placeholder="Escolha um dia" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableDates.map((d) => (
                          <SelectItem key={d} value={d}>
                            {new Date(d).toLocaleDateString('pt-BR')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowClearByDay(false)}>Cancelar</Button>
                    <Button variant="destructive" onClick={handleConfirmClearByDay} disabled={isLoading}>
                      Limpar
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <JogosGeradosModal 
            isOpen={showJogosModal}
            onClose={() => setShowJogosModal(false)}
            jogos={jogosGerados}
          />

          {/* Modal para Grupos dos Bichos */}
          <Dialog open={showAnimalGroups} onOpenChange={setShowAnimalGroups}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Grupos e Números do Jogo do Bicho</DialogTitle>
              </DialogHeader>
              <AnimalGroupsTable />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
    </>
  );
};

export default SoftwarePage;
